/**
 * TierLadder — verification ladder rebuilt on JourneyRail's node/connector
 * idiom (stepper-node-dot circles + stepper-track-fill connectors).
 *
 * Visual spec (DESIGN-phone-1/2/3):
 *   Self ── Doc ── Bank ── Lender
 *   Completed nodes = filled primary circle with checkmark.
 *   Current node    = ring-accented circle, primary text label.
 *   Future nodes    = muted empty circle.
 *   Active connectors = primary-colored line.
 *   Inactive connectors = border-colored line.
 *
 * All existing behavioral/a11y contracts preserved:
 *   - data-testid="tier-ladder" on the root <ol>
 *   - data-tier-step="<tier>" on each <li>
 *   - data-disabled / data-locked attributes on BANK/LENDER items
 *   - aria-current="step" on current filled item
 *   - aria-label on every item (current label + "(current verification level)")
 *   - Button with aria-label "Verify with <Full>" for climbable rungs
 *   - bankLoading, onClimb props unchanged; per-rung availability is the
 *     `unavailable` reason map (task 1678)
 */

import { Check } from "lucide-react";
import { cn } from "../../../../lib/utils";
import { Button } from "../../../ui/button";
import type { ProfileRowTier } from "./tier-dot";

const ORDER: ProfileRowTier[] = [
  "self-reported",
  "document-backed",
  "bank-verified",
  "lender-approved",
];

const SHORT_LABEL: Record<ProfileRowTier, string> = {
  "self-reported": "Self",
  "document-backed": "Doc",
  "bank-verified": "Secure",
  "lender-approved": "Lender",
};

const FULL_LABEL: Record<ProfileRowTier, string> = {
  "self-reported": "Self-reported",
  "document-backed": "Document-backed",
  "bank-verified": "Securely verified",
  "lender-approved": "Pre-approved",
};

// Pre-approval is not built for ANY row yet, so its copy is a property of
// the product, not of the input — unlike the DOC and BANK rungs, whose
// reasons are per-field and authored by the server (task 1678).
const LENDER_LOCKED_TITLE = "Pre-approval is coming soon";

export interface TierLadderProps {
  current: ProfileRowTier;
  /**
   * Fires when the user clicks a non-current, non-locked rung. The
   * callback receives the tier the user wants to start verifying at.
   */
  onClimb?: (target: ProfileRowTier) => void;
  /**
   * Rungs this input has no path to, mapped to the reason WHY — rendered
   * verbatim as the rung's tooltip. A rung listed here is drawn but not
   * climbable.
   *
   * A map rather than the pair of `bankDisabled`/`docDisabled` booleans it
   * replaces, because the reason has to travel with the fact. Those booleans
   * left this file authoring its own copy, and it got two things wrong that
   * the caller knew and had no way to say:
   *
   *   - the server now authors these reasons (task 1678) and the Edit &
   *     Verify drawer renders them verbatim, so a locally-written tooltip
   *     could contradict the drawer one click away. It did: for "How you
   *     earn" the server says a document cannot prove a choice, while the
   *     tooltip said "not yet".
   *   - a rung disabled by a ROLLOUT gate (bank income behind its PostHog
   *     flag) got the absence copy "Plaid doesn't cover it" — which is false.
   *     Plaid covers it; we have not turned it on.
   *
   * `lender-approved` stays a hardcoded lock: it is unavailable for every
   * row, which makes it a property of the product, not of the input.
   */
  unavailable?: Partial<Record<ProfileRowTier, string>>;
  /**
   * When true, the BANK climb button renders in a loading state.
   */
  bankLoading?: boolean;
}

export function TierLadder({
  current,
  onClimb,
  unavailable,
  bankLoading,
}: TierLadderProps) {
  const currentIdx = ORDER.indexOf(current);

  return (
    <ol
      data-testid="tier-ladder"
      className="flex items-center"
      aria-label="Verification ladder"
    >
      {ORDER.map((tier, idx) => {
        const filled = idx <= currentIdx;
        const isCurrent = idx === currentIdx;
        const isLast = idx === ORDER.length - 1;
        const lockedLender = tier === "lender-approved";
        const unavailableReason = unavailable?.[tier];
        // KEY PRESENCE, not truthiness. A caller that knows a rung is
        // unreachable but has no reason text to show maps it to "" — and an
        // empty string is falsy, so testing the reason itself would leave
        // that rung climbable. Disabling is the requirement; the tooltip is
        // the nice-to-have.
        const isUnavailable = unavailableReason !== undefined;

        // A rung is interactive when:
        //   - it's not already filled
        //   - it's not locked (Lender always; Bank for plaid-disabled rows)
        //   - a click handler exists
        const interactive =
          !filled && Boolean(onClimb) && !lockedLender && !isUnavailable;

        // Connector between this node and the next (skip last)
        const connectorFilled = idx < currentIdx;

        const stepAttrs: Record<string, string> = {};
        if (isUnavailable) {
          stepAttrs["data-disabled"] = "true";
          if (unavailableReason) stepAttrs.title = unavailableReason;
        }
        if (lockedLender) {
          stepAttrs["data-locked"] = "true";
          stepAttrs.title = LENDER_LOCKED_TITLE;
        }

        return (
          <li
            key={tier}
            data-tier-step={tier}
            className="flex items-center"
            {...stepAttrs}
          >
            {/* The node itself */}
            <NodeButton
              tier={tier}
              filled={filled}
              isCurrent={isCurrent}
              interactive={interactive}
              lockedLender={lockedLender}
              lockedUnavailable={isUnavailable}
              bankLoading={bankLoading ?? false}
              onClimb={onClimb}
            />

            {/* Connector line to the next node */}
            {!isLast && (
              <div
                aria-hidden="true"
                className={cn(
                  "h-0.5 w-6 flex-shrink-0 transition-colors duration-200",
                  connectorFilled ? "bg-primary" : "bg-border",
                )}
              />
            )}
          </li>
        );
      })}
    </ol>
  );
}

// ─── Node sub-component ──────────────────────────────────────────────────────

interface NodeButtonProps {
  tier: ProfileRowTier;
  filled: boolean;
  isCurrent: boolean;
  interactive: boolean;
  lockedLender: boolean;
  /**
   * The rung is rendered but cannot be climbed because this input has no
   * path to that tier — no Plaid coverage (bank) or no document Sill can
   * parse into it (doc). Distinct from lockedLender, which is "not built
   * yet" for every row.
   */
  lockedUnavailable: boolean;
  bankLoading: boolean;
  onClimb?: (target: ProfileRowTier) => void;
}

function NodeButton({
  tier,
  filled,
  isCurrent,
  interactive,
  lockedLender,
  lockedUnavailable,
  bankLoading,
  onClimb,
}: NodeButtonProps) {
  const label = SHORT_LABEL[tier];
  const fullLabel = FULL_LABEL[tier];

  if (filled) {
    // Completed or current node — filled primary circle (glyph only, label below)
    // "Self" node (index 0) is always achieved — solid green ring + green check.
    const isSelf = tier === "self-reported";
    return (
      <div className="flex flex-col items-center gap-1.5">
        <span
          aria-current={isCurrent ? "step" : undefined}
          aria-label={
            isCurrent
              ? `${fullLabel} (current verification level)`
              : fullLabel
          }
          data-tier={tier}
          className={cn(
            "grid h-9 w-9 place-items-center rounded-full border transition-all duration-200",
            isCurrent && !isSelf
              ? // Current (non-Self): primary-outlined ring with shadow
                "border-primary bg-card text-primary shadow-[0_0_0_4px_color-mix(in_oklch,var(--primary)_14%,transparent)]"
              : // Self node OR past completed: solid primary fill (always green)
                "border-primary bg-primary text-primary-foreground",
          )}
        >
          <Check aria-hidden="true" className="h-4 w-4" strokeWidth={2.5} />
        </span>
        <span
          className={cn(
            "text-[11px] font-medium leading-none",
            isCurrent || isSelf ? "text-primary font-semibold" : "text-muted-foreground",
          )}
        >
          {label}
        </span>
      </div>
    );
  }

  if (interactive) {
    // Climbable future node — ghost circle, interactive; circle contains no text
    return (
      <div className="flex flex-col items-center gap-1.5">
        <Button
          aria-label={`Verify with ${fullLabel}`}
          className={cn(
            "h-9 w-9 rounded-full border border-border p-0 text-muted-foreground hover:border-primary/40 hover:bg-primary/5 hover:text-primary",
          )}
          data-slot="tier-ladder-climb"
          loading={tier === "bank-verified" ? bankLoading : false}
          onClick={() => onClimb?.(tier)}
          size="sm"
          variant="outline"
        >
          {/* circle is empty when not loading; spinner shows during load */}
          {bankLoading && tier === "bank-verified" ? null : null}
        </Button>
        <span className="text-[11px] font-medium leading-none text-muted-foreground/60">
          {label}
        </span>
      </div>
    );
  }

  // Locked or future non-interactive node — empty circle, label below only
  return (
    <div className="flex flex-col items-center gap-1.5">
      <span
        aria-label={
          lockedLender
            ? `${fullLabel} — coming soon`
            : lockedUnavailable
              ? `${fullLabel} — not available for this input`
              : fullLabel
        }
        className={cn(
          "grid h-9 w-9 place-items-center rounded-full border border-border bg-card",
          (lockedLender || lockedUnavailable) &&
            "cursor-not-allowed opacity-50",
        )}
      >
        {/* no text inside the circle */}
      </span>
      <span className="text-[11px] font-medium leading-none text-muted-foreground/50">
        {label}
      </span>
    </div>
  );
}
